import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Video, Scissors, Download, Image, Calendar, BarChart3, PieChart, RefreshCw, Youtube, RotateCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from './LoadingScreen';
import { optimizedApi } from '../utils/apiOptimizer';

interface AnalyticsData {
  total_videos: number;
  total_clips: number;
  total_exports: number;
  total_downloads: number;
  total_conversions: number;
  smart_thumbnails: number;
  platform_breakdown: Record<string, number>;
  daily_activity: Array<{
    date: string;
    videos: number;
    clips: number;
    exports: number;
    downloads: number;
    conversions: number;
  }>;
  export_formats: Record<string, number>;
  download_formats: Record<string, number>;
  conversion_formats: Record<string, number>;
  period_days: number | 'all';
}

export default function AnalyticsDashboard() {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const { currentUser } = useAuth();

  const fetchAnalytics = useCallback(async (days: string) => {
    if (!currentUser) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use optimized API with caching
      const result = await optimizedApi.getAnalytics(days);
      setAnalyticsData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchAnalytics(selectedPeriod);
    }
  }, [selectedPeriod, currentUser, fetchAnalytics]);

  // Prefetch analytics data for other time periods when component mounts
  useEffect(() => {
    if (currentUser && analyticsData) {
      // Prefetch other common time periods
      const periods = ['7', '30', '90', '365'];
      periods.forEach(period => {
        if (period !== selectedPeriod) {
          optimizedApi.prefetch.analyticsData(period);
        }
      });
    }
  }, [currentUser, analyticsData, selectedPeriod]);

  if (loading) {
    return (
      <LoadingScreen
        message="Loading Analytics"
        submessage="Gathering your performance data..."
        type="general"
      />
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        {/* Animated dot background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        
        <div className="relative z-10 text-center py-8">
          <div className="max-w-md mx-auto">
            <div className="mb-6">
              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Analytics</h3>
              <p className="text-red-600 mb-6">{error}</p>
            </div>
            <button 
              onClick={() => {
                // Clear cache for analytics to force fresh data
                optimizedApi.cache.invalidate('analytics');
                fetchAnalytics(selectedPeriod);
              }}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium inline-flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Try Again</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        {/* Animated dot background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        
        <div className="relative z-10 text-center py-8">
          <div className="max-w-md mx-auto">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Analytics Data</h3>
            <p className="text-gray-600">No analytics data available for the selected period</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate some derived metrics with safe defaults
  const totalDownloads = analyticsData.total_downloads || 0;
  const totalConversions = analyticsData.total_conversions || 0;
  
  const avgClipsPerVideo = analyticsData.total_videos > 0 ? (analyticsData.total_clips / analyticsData.total_videos).toFixed(1) : '0';
  const exportRate = analyticsData.total_clips > 0 ? ((analyticsData.total_exports / analyticsData.total_clips) * 100).toFixed(1) : '0';
  const smartThumbnailUsage = analyticsData.total_videos > 0 ? ((analyticsData.smart_thumbnails / analyticsData.total_videos) * 100).toFixed(1) : '0';
  const totalToolsUsage = totalDownloads + totalConversions;
  const downloadToConversionRatio = totalConversions > 0 ? (totalDownloads / totalConversions).toFixed(1) : '0';

  return (
    <div className="relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
      {/* Animated dot background */}
      <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
      
      <div className="relative z-10 space-y-6 p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-50 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-red-500" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              </div>
              <p className="text-gray-600 text-lg">Track your content performance and engagement metrics</p>
            </div>
            
            <div className="flex items-center gap-4">
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
          >
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="365">1 year</option>
            <option value="all">All time</option>
          </select>
          
              <button
                onClick={() => {
                  // Clear cache for current analytics period to get fresh data
                  optimizedApi.cache.invalidate(`analytics_${selectedPeriod}`);
                  fetchAnalytics(selectedPeriod);
                }}
                className="p-2 text-gray-600 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                title="Refresh data"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Videos</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.total_videos}</p>
              <p className="text-xs text-gray-500">
                {analyticsData.period_days === 'all' ? 'All time' : `Last ${analyticsData.period_days} days`}
              </p>
            </div>
            <Video className="h-8 w-8 text-black" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Clips Generated</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.total_clips}</p>
              <p className="text-xs text-gray-500">AI-powered highlights</p>
            </div>
            <Scissors className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Exports</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.total_exports}</p>
              <p className="text-xs text-gray-500">Downloaded clips</p>
            </div>
            <Download className="h-8 w-8 text-gray-700" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">YouTube Downloads</p>
              <p className="text-2xl font-bold text-gray-900">{totalDownloads}</p>
              <p className="text-xs text-gray-500">Videos downloaded</p>
            </div>
            <Youtube className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Clip Conversions</p>
              <p className="text-2xl font-bold text-gray-900">{totalConversions}</p>
              <p className="text-xs text-gray-500">Clips converted</p>
            </div>
            <RotateCw className="h-8 w-8 text-gray-700" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Smart Thumbnails</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.smart_thumbnails}</p>
              <p className="text-xs text-gray-500">AI-selected thumbnails</p>
            </div>
            <Image className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Platform Breakdown */}
      {Object.keys(analyticsData.platform_breakdown).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Platform Breakdown</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(analyticsData.platform_breakdown).map(([platform, count]) => (
              <div key={platform} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-600 capitalize">{platform}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Export Formats */}
      {Object.keys(analyticsData.export_formats).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Export Formats</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(analyticsData.export_formats).map(([format, count]) => (
              <div key={format} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-600 uppercase">{format}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* YouTube Download Formats */}
      {analyticsData.download_formats && Object.keys(analyticsData.download_formats).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Youtube className="h-5 w-5 text-red-500" />
            <h3 className="text-lg font-semibold text-gray-900">YouTube Download Formats</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(analyticsData.download_formats).map(([format, count]) => (
              <div key={format} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-600 uppercase">{format}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clip Conversion Formats */}
      {analyticsData.conversion_formats && Object.keys(analyticsData.conversion_formats).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <RotateCw className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Clip Conversion Formats</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(analyticsData.conversion_formats).map(([format, count]) => (
              <div key={format} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-sm text-gray-600 uppercase">{format}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Performance Summary</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-3xl font-bold text-black">{avgClipsPerVideo}</p>
            <p className="text-sm text-gray-700 font-medium">Avg Clips per Video</p>
            <p className="text-xs text-gray-600 mt-1">Higher is better for content variety</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-3xl font-bold text-red-600">{exportRate}%</p>
            <p className="text-sm text-gray-700 font-medium">Export Rate</p>
            <p className="text-xs text-gray-600 mt-1">Clips downloaded vs generated</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-3xl font-bold text-gray-900">{totalToolsUsage}</p>
            <p className="text-sm text-gray-700 font-medium">Total Tools Usage</p>
            <p className="text-xs text-gray-600 mt-1">Downloads + Conversions</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-3xl font-bold text-gray-900">{downloadToConversionRatio}</p>
            <p className="text-sm text-gray-700 font-medium">Download/Conversion Ratio</p>
            <p className="text-xs text-gray-600 mt-1">YouTube vs Clip Converter usage</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-3xl font-bold text-black">{smartThumbnailUsage}%</p>
            <p className="text-sm text-gray-700 font-medium">Smart Thumbnail Usage</p>
            <p className="text-xs text-gray-600 mt-1">AI-optimized thumbnails</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {analyticsData.daily_activity.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          </div>
          <div className="space-y-2">
            {analyticsData.daily_activity.slice(-7).map((day) => (
              <div key={day.date} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                <span className="text-sm text-gray-600">
                  {new Date(day.date).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-black font-medium">{day.videos || 0} videos</span>
                  <span className="text-red-600 font-medium">{day.clips || 0} clips</span>
                  <span className="text-gray-700 font-medium">{day.exports || 0} exports</span>
                  <span className="text-red-500 font-medium">{day.downloads || 0} downloads</span>
                  <span className="text-gray-700 font-medium">{day.conversions || 0} conversions</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
} 